// The make dashboard (ERP-PLAN §5.4 "Make dashboard"), shared by the page and the MCP's make_readiness, worked
// out from the plan (shared/planning.ts with BOMs: the MRP) and the synced stock:
// - per make item: weeks of cover and the next build (quantity, start, finished) from the plan
// - component readiness for the CM's builds in start order (open make POs first): each part comes from what's at
//   the CM, then POs shipping there, then stock at HHL (to ship over), then POs to HHL, and the rest is short; the
//   part that's ready last gates the build, with its ETA (a PO's date, or the plan's next order for it)
// - what the CM can build today from what's already there (buildable() over the CM's stock only)
// - the consignment plan: per CM, what HHL must buy or ship there and by when, with the plan's orders for it
// - a timeline per project: part arrivals at the CM, builds, and finished goods landing at HHL. A sell item is in its
//   project; a sub-assembly in each project whose BOMs use it (shared/project-reach.ts), so a shared board is in each
//   of their timelines.
// Days are projection days (0 = today), like the plan's.
// With consignment's summary (`cms`, summarizeCms() in shared/consignment.ts) the CM's stock, what its open make
// orders have set aside, their units still to build and BOM snapshots come from there, so the two pages agree: open
// make orders hold what they reserved, and planned builds (and "build today") start from the CM's free stock.
import { type BomIndex, type BomItem, type Buildable, buildable } from "./boms.ts";
import { type ClosureDelay, delayFor } from "./closures.ts";
import { type CmSummary, isConsigned, type MakeOrderState } from "./consignment.ts";
import type { Location, ProductionRun, Project, ProjectProduct, StockBalance, StockLevel } from "./db.gen.ts";
import type { MakeSpec } from "./mrp.ts";
import {
  dayNum,
  type Incoming,
  type ItemPlanResult,
  type MrpOrder,
  orderGroupKey,
  type Plan,
  type PlanItem,
  type PlanLink,
  type PlanVendor,
} from "./planning.ts";
import { projectReach } from "./project-reach.ts";
import { backersLeft } from "./receiving.ts";

export type MakeData = {
  plan: Plan;
  boms: BomIndex;
  locations: readonly Pick<Location, "id" | "name" | "kind" | "vendor_id" | "active" | "ignored">[];
  stock: readonly Pick<StockLevel, "item_id" | "location_id" | "on_hand" | "available">[];
  balances: readonly Pick<StockBalance, "item_id" | "location_id" | "qty">[];
  vendors: readonly PlanVendor[];
  projects: readonly Pick<Project, "id" | "name">[];
  /** The products' project links: a sub-assembly's projects come from the BOMs that use it. */
  projectProducts: readonly Pick<ProjectProduct, "shopify_product_id" | "project_id">[];
  /** Consignment's per-CM-site summary (useCms() / loadCms()). */
  cms?: ReadonlyMap<string, CmSummary>;
};

/** One build at a CM: an open make PO still to finish, or the plan's next build. */
export type Build = {
  key: string;
  itemId: string;
  qty: number;
  start: number;
  end: number;
  /** An open make PO; null = the plan's next build. */
  poNumber: string | null;
  /** Where the finished units go (HHL, or the CM that uses a sub-assembly). */
  shipTo: string | null;
  draft: boolean;
  order: MrpOrder | null;
  /** An open make order (consignment): what it still needs and has set aside at the CM. */
  makeOrder: MakeOrderState | null;
};

export type ReadinessLine = {
  itemId: string;
  item: BomItem | undefined;
  perUnit: number;
  need: number;
  /** Covered by what's at the CM now. */
  atCm: number;
  /** Covered by POs shipping to the CM, and when the last of them lands. */
  toCm: number;
  toCmEta: number | null;
  /** Covered by stock at HHL (to ship to the CM). */
  atHhl: number;
  /** Covered by POs to HHL (then shipped on), and when. */
  onOrder: number;
  onOrderEta: number | null;
  short: number;
  /** When the short part could be there: the plan's next order for it, else ordered today. */
  shortEta: number | null;
  /** The day all of it is there (0 = today); null = short with nothing to go by. */
  readyDay: number | null;
};

export type Readiness = {
  build: Build;
  lines: ReadinessLine[];
  /** The part that's there last (or short with no date), which decides when the build can start. */
  gating: ReadinessLine | null;
  readyDay: number | null;
  /** Everything is at the CM or will be by the build's start. */
  ready: boolean;
  shortParts: number;
  /** Parts covered only by stock at HHL (or POs to HHL): they still have to be shipped to the CM. */
  toShip: number;
};

export type MakeRow = {
  result: ItemPlanResult;
  item: PlanItem;
  spec: MakeSpec;
  vendor: PlanVendor | null;
  site: string | null;
  /** A sell item's project; a sub-assembly's projects (in use, then only on a draft), by name. */
  projectIds: string[];
  /**
   * The projects whose runs can build it, as a run's plan checks it: a sell item's own (item.project_id, not its BOM's
   * project), a sub-assembly's projects.
   */
  runProjectIds: string[];
  coverWeeks: number | null;
  next: MrpOrder | null;
  /** Make POs for it still to finish (drafts too). */
  openBuilds: Incoming[];
  /** Their make orders at the CM (consignment), open ones first. */
  makeOrders: MakeOrderState[];
  readiness: Readiness | null;
  /** From what's at the CM now and not set aside for open make orders (CM-bought parts and services never limit). */
  buildableNow: Buildable;
};

export type ConsignLine = {
  itemId: string;
  item: BomItem | undefined;
  result: ItemPlanResult | undefined;
  need: number;
  atCm: number;
  toCm: number;
  /** Ship over from HHL's stock. */
  shipFromHhl: number;
  /** On POs to HHL: forward when they land. */
  forward: number;
  /** HHL must buy this much more for the builds. */
  buy: number;
  /** When it must be at the CM: the first build that needs more than is there. */
  byDay: number;
  /** The last day to order the part to have it by byDay: its lead time before, earlier across a vendor closure (below 0 = late). */
  orderBy: number | null;
  /** Vendor closures --- what its vendor's closures did to that order-by (null: nothing). */
  delay: ClosureDelay | null;
  /** The plan's next order for it to this CM (what "Create draft POs" would order). */
  order: MrpOrder | null;
  builds: string[];
};

export type CmView = {
  vendor: PlanVendor | null;
  vendorId: string;
  site: string | null;
  siteName: string;
  rows: MakeRow[];
  builds: Readiness[];
  consignment: ConsignLine[];
};

export type TimelineEvent =
  | { kind: "build"; from: number; to: number; label: string; planned: boolean; poNumber: string | null }
  | { kind: "arrival"; day: number; label: string; planned: boolean; qty: number }
  | { kind: "ship"; day: number; label: string; planned: boolean };

export type TimelineRow = { row: MakeRow; events: TimelineEvent[] };
export type ProjectTimeline = { projectId: string | null; name: string; rows: TimelineRow[]; to: number };

export type MakeDashboard = { rows: MakeRow[]; cms: CmView[]; timelines: ProjectTimeline[]; byItem: Map<string, MakeRow> };

const EPS = 1e-9;

/**
 * Pre-sales --- an open make PO line as people read it: units still to build (its make order's, else what HHL still
 * expects) and how many of those are for backers. Never "PO-1018 · 0" for a line that only has backers' units left.
 */
export function buildLeft(row: Pick<MakeRow, "makeOrders">, x: Incoming): { toBuild: number; forBackers: number } {
  const mo = row.makeOrders.find((o) => o.mo.po_line_id === x.lineId);
  if (!mo) return { toBuild: x.qty, forBackers: 0 };
  const forBackers = mo.line ? Math.min(mo.remainingUnits, backersLeft({ ...mo.line, qty_output: mo.output })) : 0;
  return { toBuild: mo.remainingUnits, forBackers };
}

/** "PO-1018 · 150 to build (99 for backers)", or "PO-1010 · built, 50 to come". */
export function buildText(row: Pick<MakeRow, "makeOrders">, x: Incoming): string {
  const { toBuild, forBackers } = buildLeft(row, x);
  const po = `${x.poNumber}${x.draft ? " (draft)" : ""}`;
  // All built: what's still to come to HHL.
  if (toBuild <= 0) return `${po} · built${x.qty > 0 ? `, ${fmtQty(x.qty)} to come` : ""}`;
  return `${po} · ${fmtQty(toBuild)} to build${forBackers > 0 ? ` (${fmtQty(forBackers)} for backers)` : ""}`;
}

export function makeDashboard(d: MakeData): MakeDashboard {
  const { plan, boms: ix } = d;
  const t0 = dayNum(plan.today);
  const used = (l: MakeData["locations"][number]) => l.active && !l.ignored;
  // A CM's sites: consignment's CM sites when it has any, else its vendor sites.
  const sitesOf = new Map<string, string[]>();
  for (const l of d.locations) {
    if (l.kind !== "vendor_site" || !l.vendor_id || !used(l)) continue;
    if (d.cms && [...d.cms.values()].some((c) => c.location.vendor_id === l.vendor_id) && !d.cms.has(l.id)) continue;
    sitesOf.set(l.vendor_id, [...(sitesOf.get(l.vendor_id) ?? []), l.id]);
  }
  const cmSite = new Set([...sitesOf.values()].flat());
  const hhl = new Set(d.locations.filter((l) => used(l) && (l.kind === "hhl" || l.kind === "in_transit")).map((l) => l.id));
  const nameOf = new Map(d.locations.map((l) => [l.id, l.name]));
  // Stock per location and item: Shopify's levels plus the ledger. At HHL Shopify's available (units on open orders are
  // sold, not for builds); at a CM site what's there.
  const at = new Map<string, Map<string, number>>();
  const add = (loc: string, item: string, q: number) => {
    let m = at.get(loc);
    if (!m) at.set(loc, (m = new Map()));
    m.set(item, (m.get(item) ?? 0) + q);
  };
  for (const s of d.stock) add(s.location_id, s.item_id, Number(hhl.has(s.location_id) ? s.available : s.on_hand));
  for (const b of d.balances) add(b.location_id, b.item_id, Number(b.qty));
  const stockIn = (locs: Iterable<string>, item: string) => {
    let n = 0;
    for (const l of locs) n += at.get(l)?.get(item) ?? 0;
    return n;
  };
  // At a CM site consignment knows, what's free: its stock (in transit to it included) less what open orders set aside.
  const free = new Map<string, Map<string, number>>();
  for (const [id, c] of d.cms ?? []) free.set(id, new Map(c.stock.map((l) => [l.itemId, Math.max(0, l.free)])));
  const freeIn = (locs: Iterable<string>, item: string) => {
    let n = 0;
    for (const l of locs) n += free.has(l) ? (free.get(l)?.get(item) ?? 0) : (at.get(l)?.get(item) ?? 0);
    return n;
  };
  const ordersOf = new Map<string, MakeOrderState[]>();
  for (const c of d.cms?.values() ?? []) for (const o of c.orders) ordersOf.set(o.mo.item_id, [...(ordersOf.get(o.mo.item_id) ?? []), o]);
  const vendorOf = new Map(d.vendors.map((v) => [v.id, v]));
  // A sub-assembly is in the projects whose builds use it.
  const projectName = new Map(d.projects.map((p) => [p.id, p.name]));
  const linkOf = new Map(d.projectProducts.map((l) => [l.shopify_product_id, l.project_id]));
  const reach = projectReach(
    ix,
    (productId) => linkOf.get(productId),
    (id) => projectName.get(id) ?? id,
  );
  const projectsOf = (item: BomItem | undefined, spec: MakeSpec): string[] => {
    if (item?.source === "local") {
      const p = reach.byPart.get(item.id);
      return p ? [...p.inUse, ...p.onDraft.filter((x) => !p.inUse.includes(x))] : [];
    }
    const own = item?.project_id ?? spec.bom.project_ids[0];
    return own ? [own] : [];
  };
  const runProjectsOf = (item: BomItem | undefined, shown: string[]): string[] =>
    item?.source === "local" ? shown : item?.project_id ? [item.project_id] : [];

  const rows: MakeRow[] = [];
  for (const r of plan.results) {
    if (r.role !== "make" || !r.make) continue;
    const spec = r.make;
    const sites = sitesOf.get(spec.vendorId) ?? [];
    const bomItem = ix.itemById.get(r.item.id);
    // What the CM could build now: the BOM resolved as usual, over the free stock at the CM's sites only.
    const cmStock = new Map<string, number>();
    for (const l of sites) for (const [item, q] of free.get(l) ?? at.get(l) ?? []) cmStock.set(item, (cmStock.get(item) ?? 0) + q);
    const projectIds = projectsOf(bomItem, spec);
    rows.push({
      result: r,
      item: r.item,
      spec,
      vendor: r.vendor ?? vendorOf.get(spec.vendorId) ?? null,
      site: plan.mrp?.siteOf.get(spec.vendorId) ?? sites[0] ?? null,
      projectIds,
      runProjectIds: runProjectsOf(bomItem, projectIds),
      coverWeeks: r.coverDays === null ? null : r.coverDays / 7,
      next: r.orders[0] ?? null,
      // Pre-sales --- every open make PO line with building left, one all for backers too.
      openBuilds: r.builds ?? r.incoming.filter((x) => x.kind === "make"),
      makeOrders: (ordersOf.get(r.item.id) ?? []).filter((o) => o.open || o.status === "built"),
      readiness: null,
      buildableNow: buildable({ ...ix, onHand: cmStock }, spec.bom, bomItem),
    });
  }

  // Readiness. Pass 1, every CM's builds in start order: each part from what's at that CM (or set aside for an open
  // make order), then POs shipping there, then HHL's stock and POs to HHL. HHL's side is one pool for all CMs, and only
  // what its own sales don't need (safety stock, B2B and the forecast over its lead time) is offered to builds.
  const cms: CmView[] = [];
  const byCm = new Map<string, MakeRow[]>();
  for (const row of rows) byCm.set(row.spec.vendorId, [...(byCm.get(row.spec.vendorId) ?? []), row]);
  type Dated = { qty: number; day: number };
  type Raw = Omit<ReadinessLine, "shortEta" | "readyDay">;
  const all: { row: MakeRow; build: Build; vendorId: string; raw: Raw[] }[] = [];
  for (const [vendorId, cmRows] of byCm) {
    for (const row of cmRows) {
      for (const x of row.openBuilds) {
        const end = dayNum(x.arrive) - t0;
        // With its make order: the units still to build, and what consignment set aside for them.
        const mo = row.makeOrders.find((o) => o.mo.po_line_id === x.lineId) ?? null;
        if (mo && !mo.open) continue;
        all.push({
          row,
          vendorId,
          raw: [],
          build: {
            key: `po:${x.lineId}`,
            itemId: row.item.id,
            qty: mo ? mo.remainingUnits : x.qty,
            start: Math.max(0, row.result.startBy(end)), // Vendor closures --- the build time plus the CM's days closed
            end,
            poNumber: x.poNumber,
            shipTo: x.shipTo ?? null,
            draft: x.draft,
            order: null,
            makeOrder: mo,
          },
        });
      }
      if (row.next) {
        const o = row.next;
        all.push({
          row,
          vendorId,
          raw: [],
          build: {
            key: o.key,
            itemId: row.item.id,
            qty: o.qty,
            start: o.orderDay,
            end: o.arriveDay,
            poNumber: null,
            shipTo: o.shipTo,
            draft: false,
            order: o,
            makeOrder: null,
          },
        });
      }
    }
  }
  all.sort((a, b) => a.build.start - b.build.start || Number(!!b.build.poNumber) - Number(!!a.build.poNumber));
  const incomingOf = (itemId: string) =>
    (plan.byItem.get(itemId)?.incoming ?? []).map((x) => ({ qty: x.qty, day: dayNum(x.arrive) - t0, shipTo: x.shipTo ?? null }));
  const cmPools = new Map<string, { atCm: number; toCm: Dated[] }>();
  const cmPool = (vendorId: string, itemId: string) => {
    const key = `${vendorId}|${itemId}`;
    let p = cmPools.get(key);
    if (!p) {
      const sites = new Set(sitesOf.get(vendorId) ?? []);
      p = {
        atCm: Math.max(0, freeIn(sites, itemId)),
        toCm: incomingOf(itemId)
          .filter((x) => x.shipTo && sites.has(x.shipTo))
          .sort((a, b) => a.day - b.day),
      };
      cmPools.set(key, p);
    }
    return p;
  };
  const hhlPools = new Map<string, { atHhl: number; onOrder: Dated[] }>();
  const hhlPool = (itemId: string) => {
    let p = hhlPools.get(itemId);
    if (!p) {
      const res = plan.byItem.get(itemId);
      let ownNeed = 0;
      if (res) {
        // Pre-sales --- units of runs that aren't for sale (backers, holds) aren't offered to builds either.
        ownNeed = res.safetyStock + res.b2bOpen + (res.reservedQty ?? 0);
        // Until a new order could land (closures included: Chinese New Year makes HHL wait longer).
        for (let day = 0, until = res.landsOn(0); day < until; day++) ownNeed += res.demandOn(day);
      }
      p = {
        atHhl: Math.max(0, (res ? res.available : stockIn(hhl, itemId)) - ownNeed),
        onOrder: incomingOf(itemId)
          .filter((x) => !x.shipTo || !cmSite.has(x.shipTo))
          .sort((a, b) => a.day - b.day),
      };
      hhlPools.set(itemId, p);
    }
    return p;
  };
  const take = (list: Dated[], want: number) => {
    let got = 0;
    let eta: number | null = null;
    while (want - got > EPS && list.length) {
      const q = Math.min(list[0].qty, want - got);
      got += q;
      eta = Math.max(eta ?? 0, list[0].day);
      list[0].qty -= q;
      if (list[0].qty <= EPS) list.shift();
    }
    return { got, eta };
  };
  for (const b of all) {
    // An open make order builds from its snapshot and already holds what it reserved; planned builds take free stock.
    const mo = b.build.makeOrder;
    const parts = mo
      ? mo.components.filter((c) => isConsigned(c)).map((c) => ({ itemId: c.item_id, item: ix.itemById.get(c.item_id), qty: c.per_unit, reserved: c.reserved }))
      : b.row.spec.parts.map((p) => ({ ...p, reserved: null as number | null }));
    for (const part of parts) {
      const cp = cmPool(b.vendorId, part.itemId);
      const hp = hhlPool(part.itemId);
      const need = b.build.qty * part.qty;
      let left = need;
      const atCm = part.reserved !== null ? Math.min(need, part.reserved) : Math.min(left, cp.atCm);
      if (part.reserved === null) cp.atCm -= atCm;
      left -= atCm;
      const toCm = take(cp.toCm, left);
      left -= toCm.got;
      const atHhl = Math.min(left, hp.atHhl);
      hp.atHhl -= atHhl;
      left -= atHhl;
      const onOrder = take(hp.onOrder, left);
      left -= onOrder.got;
      b.raw.push({
        itemId: part.itemId,
        item: part.item,
        perUnit: part.qty,
        need,
        atCm,
        toCm: toCm.got,
        toCmEta: toCm.got > EPS ? toCm.eta : null,
        atHhl,
        onOrder: onOrder.got,
        onOrderEta: onOrder.got > EPS ? onOrder.eta : null,
        short: left > EPS ? left : 0,
      });
    }
  }

  // Pass 2, deepest items first: when each part is there, so a build waiting on a sub-assembly knows when that
  // sub-assembly's own next build can finish.
  const buildEta = new Map<string, number>();
  const readinessOf = new Map<(typeof all)[number], Readiness>();
  for (const b of [...all].sort((x, y) => y.row.result.level - x.row.result.level || x.build.start - y.build.start)) {
    const lines: ReadinessLine[] = b.raw.map((l) => {
      const res = plan.byItem.get(l.itemId);
      const nextOrder = res?.orders.find((o) => o.arriveDay >= 0) ?? null;
      const ordered = nextOrder ? Math.max(nextOrder.arriveDay, res?.landsOn(0) ?? 0) : res ? res.landsOn(0) : null;
      const built = buildEta.get(l.itemId);
      const shortEta = !l.short ? null : built === undefined ? ordered : Number.isFinite(built) ? Math.max(ordered ?? 0, built) : null;
      const days = [l.atCm > EPS || l.atHhl > EPS ? 0 : null, l.toCmEta, l.onOrderEta];
      const readyDay = l.short && shortEta === null ? null : Math.max(0, ...days.filter((x): x is number => x !== null), l.short ? (shortEta ?? 0) : 0);
      return { ...l, shortEta, readyDay };
    });
    // The gating part: the one there last (short with no date counts as never), the bigger shortfall on a tie.
    const rank = (x: ReadinessLine) => (x.readyDay === null ? Number.POSITIVE_INFINITY : x.readyDay);
    let gating: ReadinessLine | null = null;
    for (const l of lines) {
      if (rank(l) <= 0 && l.short <= EPS) continue;
      if (!gating || rank(l) > rank(gating) || (rank(l) === rank(gating) && l.short > gating.short)) gating = l;
    }
    const readyDay = lines.some((l) => l.readyDay === null) ? null : Math.max(0, ...lines.map((l) => l.readyDay ?? 0));
    const rd: Readiness = {
      build: b.build,
      lines,
      gating,
      readyDay,
      ready: readyDay !== null && readyDay <= Math.max(0, b.build.start),
      shortParts: lines.filter((l) => l.short > EPS).length,
      toShip: lines.filter((l) => l.atHhl + l.onOrder > EPS).length,
    };
    readinessOf.set(b, rd);
    if (b.build.order && !buildEta.has(b.row.item.id)) {
      b.row.readiness = rd;
      buildEta.set(b.row.item.id, readyDay === null ? Number.POSITIVE_INFINITY : b.row.result.landsOn(Math.max(b.build.start, readyDay)));
    }
  }

  // Per CM: its builds in start order and the consignment plan.
  for (const [vendorId, cmRows] of byCm) {
    const sites = new Set(sitesOf.get(vendorId) ?? []);
    const readiness = all.filter((b) => b.vendorId === vendorId).map((b) => readinessOf.get(b) as Readiness);
    // Readiness for a row without a planned build: its open make PO's.
    for (const row of cmRows) row.readiness ??= readiness.find((x) => x.build.itemId === row.item.id) ?? null;
    const consign = new Map<string, ConsignLine>();
    for (const rd of readiness) {
      for (const l of rd.lines) {
        // Every build's take of each part; the parts HHL must get there are kept below.
        const c = consign.get(l.itemId) ?? {
          itemId: l.itemId,
          item: l.item,
          result: plan.byItem.get(l.itemId),
          need: 0,
          atCm: 0,
          toCm: 0,
          shipFromHhl: 0,
          forward: 0,
          buy: 0,
          byDay: Number.POSITIVE_INFINITY,
          orderBy: null,
          delay: null,
          order: null,
          builds: [],
        };
        c.need += l.need;
        c.atCm += l.atCm;
        c.toCm += l.toCm;
        c.shipFromHhl += l.atHhl;
        c.forward += l.onOrder;
        c.buy += l.short;
        const label = rd.build.poNumber ?? `${plan.byItem.get(rd.build.itemId)?.item.sku ?? ""} build`;
        if (!c.builds.includes(label)) c.builds.push(label);
        // Needed there by the first build that takes more than is at the CM or on its way there.
        if (l.atHhl > EPS || l.onOrder > EPS || l.short > EPS) c.byDay = Math.min(c.byDay, rd.build.start);
        consign.set(l.itemId, c);
      }
    }
    const site = plan.mrp?.siteOf.get(vendorId) ?? [...sites][0] ?? null;
    const consignment = [...consign.values()]
      .filter((c) => c.shipFromHhl > EPS || c.forward > EPS || c.buy > EPS)
      .map((c) => {
        const order = c.result?.orders.find((o) => (o.shipTo && sites.has(o.shipTo)) || o.shipSplit.some((s) => s.shipTo && sites.has(s.shipTo))) ?? null;
        return {
          ...c,
          orderBy: c.result ? c.result.startBy(c.byDay) : null,
          delay: c.result ? delayFor(c.result.closures, c.byDay, c.result.leadTime) : null,
          order,
        };
      })
      .sort((a, b) => (a.orderBy ?? a.byDay) - (b.orderBy ?? b.byDay) || (a.item?.sku ?? "").localeCompare(b.item?.sku ?? ""));
    cms.push({
      vendor: vendorOf.get(vendorId) ?? cmRows[0]?.vendor ?? null,
      vendorId,
      site,
      siteName: site ? (nameOf.get(site) ?? "") : "",
      rows: cmRows.sort((a, b) => (a.next?.orderBy ?? 1e9) - (b.next?.orderBy ?? 1e9) || a.item.sku.localeCompare(b.item.sku)),
      builds: readiness,
      consignment,
    });
  }
  cms.sort((a, b) => (a.vendor?.short_name || a.vendor?.name || "").localeCompare(b.vendor?.short_name || b.vendor?.name || ""));

  // Timelines per project: a row in each of its projects ---
  const timelines = new Map<string, ProjectTimeline>();
  for (const cm of cms) {
    for (const row of cm.rows) {
      const events: TimelineEvent[] = [];
      for (const b of cm.builds.filter((x) => x.build.itemId === row.item.id)) {
        const label = b.build.poNumber ? `${b.build.poNumber}: ${fmtQty(b.build.qty)}` : `Build ${fmtQty(b.build.qty)}`;
        events.push({ kind: "build", from: b.build.start, to: b.build.end, label, planned: !b.build.poNumber, poNumber: b.build.poNumber });
        const place = b.build.shipTo && b.build.shipTo !== plan.mrp?.hhl ? nameOf.get(b.build.shipTo) || "the next CM" : "HHL";
        events.push({ kind: "ship", day: b.build.end, label: `${fmtQty(b.build.qty)} finished, at ${place}`, planned: !b.build.poNumber });
        for (const l of b.lines) {
          const sku = l.item?.sku ?? "part";
          if (l.toCmEta !== null) events.push({ kind: "arrival", day: l.toCmEta, label: `${sku}: ${fmtQty(l.toCm)} on PO`, planned: false, qty: l.toCm });
          if (l.onOrderEta !== null)
            events.push({ kind: "arrival", day: l.onOrderEta, label: `${sku}: ${fmtQty(l.onOrder)} to HHL, then ship on`, planned: false, qty: l.onOrder });
          if (l.short > EPS && l.shortEta !== null)
            events.push({ kind: "arrival", day: l.shortEta, label: `${sku}: ${fmtQty(l.short)} still to order`, planned: true, qty: l.short });
        }
      }
      for (const projectId of row.projectIds.length ? row.projectIds : [null]) {
        const key = projectId ?? "";
        const t = timelines.get(key) ?? {
          projectId,
          name: projectId ? (projectName.get(projectId) ?? "Project") : "No project",
          rows: [],
          to: 0,
        };
        t.rows.push({ row, events });
        for (const e of events) t.to = Math.max(t.to, e.kind === "build" ? e.to : e.day);
        timelines.set(key, t);
      }
    }
  }
  return {
    rows,
    cms,
    timelines: [...timelines.values()].sort((a, b) => Number(!a.projectId) - Number(!b.projectId) || a.name.localeCompare(b.name)),
    byItem: new Map(rows.map((r) => [r.item.id, r])),
  };
}

const fmtQty = (n: number) => Math.round(n).toLocaleString("en-US");

// Draft POs from planned orders ------------------------------------------------------------------------

export type DraftPick = { result: ItemPlanResult; qty: number; shipTo: string | null; kind: MrpOrder["kind"] };
export type DraftGroup = {
  key: string;
  vendor: PlanVendor;
  link: (r: ItemPlanResult) => PlanLink | null;
  shipTo: string | null;
  kind: MrpOrder["kind"];
  picks: DraftPick[];
};

/**
 * Production runs --- the run a build group's make PO is for: the one run at its CM still planning or ordering with no
 * make PO yet, when every pick is in that run's project and no other (`projectIdsOf`: MakeRow.runProjectIds). A board two
 * projects use (one only on a draft too), picks of two projects or two such runs: no run, and `could` has the runs it
 * might be for, to say so (link it on the PO), so a build's fee never lands on the wrong run.
 */
export function runForBuilds<R extends Pick<ProductionRun, "id" | "project_id" | "status" | "cm_vendor_id">>(
  g: Pick<DraftGroup, "kind" | "vendor" | "picks">,
  runs: readonly R[],
  pos: readonly { production_run_id: string | null; kind: string; status: string }[],
  projectIdsOf: (itemId: string) => readonly string[],
): { run: R | undefined; could: R[] } {
  if (g.kind !== "make") return { run: undefined, could: [] };
  const ids = g.picks.map((p) => projectIdsOf(p.result.item.id));
  // Open runs at the CM a pick's projects have.
  const could = runs.filter(
    (r) =>
      (r.status === "planning" || r.status === "ordering") &&
      r.cm_vendor_id === g.vendor.id &&
      ids.some((list) => list.includes(r.project_id)) &&
      !pos.some((po) => po.production_run_id === r.id && po.kind === "make" && po.status !== "cancelled"),
  );
  const only = could.length === 1 && ids.every((list) => list.length === 1 && list[0] === could[0].project_id);
  return only ? { run: could[0], could: [] } : { run: undefined, could };
}

/**
 * Where a build group goes (Mitch, 2026-11-04: manufacturing around runs; the Make plan is advice that starts runs, never
 * orders of their own): onto the one open run it fits (`join`, runForBuilds()); with none open, a new run of its
 * project at its CM (`start`) when every pick is that one project's; otherwise nowhere (`none`, with the runs it could
 * be for), so no make order is ever made outside a run. A parts group is a plain PO (`po`).
 */
export function runPlanForBuilds<R extends Pick<ProductionRun, "id" | "project_id" | "status" | "cm_vendor_id">>(
  g: Pick<DraftGroup, "kind" | "vendor" | "picks">,
  runs: readonly R[],
  pos: readonly { production_run_id: string | null; kind: string; status: string }[],
  projectIdsOf: (itemId: string) => readonly string[],
): { po: true } | { join: R } | { start: string } | { none: R[] } {
  if (g.kind !== "make") return { po: true };
  const { run, could } = runForBuilds(g, runs, pos, projectIdsOf);
  if (run) return { join: run };
  if (could.length) return { none: could };
  const ids = g.picks.map((p) => projectIdsOf(p.result.item.id));
  const only = ids[0]?.length === 1 ? ids[0][0] : null;
  return only && ids.every((list) => list.length === 1 && list[0] === only) ? { start: only } : { none: [] };
}

/** Chosen planned orders, one group (one draft PO) per vendor, ship-to and kind; ones without a vendor are left out. */
export function draftGroups(picks: readonly DraftPick[]): { groups: DraftGroup[]; noVendor: DraftPick[] } {
  const groups = new Map<string, DraftGroup>();
  const noVendor: DraftPick[] = [];
  for (const p of picks) {
    if (p.qty <= 0) continue;
    const vendor = p.result.vendor;
    if (!vendor) {
      noVendor.push(p);
      continue;
    }
    const key = orderGroupKey(vendor.id, p.shipTo, p.kind);
    const g = groups.get(key) ?? { key, vendor, link: (r: ItemPlanResult) => r.link, shipTo: p.shipTo, kind: p.kind, picks: [] };
    g.picks.push(p);
    groups.set(key, g);
  }
  return { groups: [...groups.values()], noVendor };
}
